import { CandidatesTab } from '@/app/equipmentTypes/CandidatesTab';
import { CatalogTab } from '@/app/equipmentTypes/CatalogTab';
import { FAMILIES_PATH, FamiliesTab } from '@/app/equipmentTypes/FamiliesTab';
import { RetiredTab } from '@/app/equipmentTypes/RetiredTab';
import { useSpaceFilter } from '@/app/equipmentTypes/SpaceFilterPills';
import EquipmentTypeForm from '@/app/itemType/EquipmentTypeForm';
import { PageHeader, PageShell } from '@/components/PageShell';
import { TabNav } from '@/components/TabNav';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useDiscardEquipmentType } from '@/db/useDiscardEquipmentType';
import {
    allEquipmentTypesState,
    allEquipmentTypesWithDraftsState,
    equipmentTypeCandidatesState,
    equipmentTypeFamiliesState,
} from '@/state';
import { useLoadableAtomValue } from '@/state/loadableHooks';
import logger from '@/util/logger';
import { EquipmentTypeDao } from '@rock/db-admin';
import type { EquipmentType, EquipmentTypeId } from '@rock/types';
import { type FC, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

const log = logger('EquipmentTypesPage');
const equipmentTypeDao = new EquipmentTypeDao();

/**
 * The catalog of equipment types, next to the customs people asked for that aren't in it yet —
 * two answers to the same question, so they share one page — the types retired out of it, kept
 * where they can be looked at and restored, and the families of types one product may be filed under.
 * Tabs come first and each owns its filters below them; the tab counts are the unfiltered totals.
 */
export const EquipmentTypesPage: FC<{ tab: 'catalog' | 'candidates' | 'retired' | 'families' }> = ({ tab }) => {
    const [allTypes, typesLoadable] = useLoadableAtomValue(allEquipmentTypesWithDraftsState, []);
    const [liveTypes, liveLoadable] = useLoadableAtomValue(allEquipmentTypesState, []);
    const [families, familiesLoadable] = useLoadableAtomValue(equipmentTypeFamiliesState, []);
    // Only the live doc's retirement counts: a draft overlaying it may carry a missing or stale copy.
    const { types, retiredTypes } = useMemo(() => {
        const retired = liveTypes.filter((t) => !!t.retiredAt);
        const retiredIds = new Set(retired.map((t) => t.id));
        return { types: allTypes.filter((t) => !retiredIds.has(t.id)), retiredTypes: retired };
    }, [allTypes, liveTypes]);
    const typesLoaded = typesLoadable.state === 'hasData' && liveLoadable.state === 'hasData';
    const [candidates, candidatesLoadable] = useLoadableAtomValue(equipmentTypeCandidatesState, []);
    // Tab links carry the query string across, so switching tabs keeps the space you picked.
    const [searchParams] = useSearchParams();
    const tabSearch = searchParams.toString() ? `?${searchParams}` : '';
    const spaceFilter = useSpaceFilter();

    const [editingTypeId, setEditingTypeId] = useState<undefined | null | EquipmentTypeId>();
    const [saving, setSaving] = useState(false);
    const { discardEquipmentType } = useDiscardEquipmentType();

    async function saveEquipmentType(type: EquipmentType) {
        setSaving(true);
        try {
            // Edits stage to the draft — publish to preview/live from the equipment-type page.
            await equipmentTypeDao.setEquipmentTypeDraft(type.id, type);

            toast.success('Saved draft', { description: 'Publish to preview/live from the type page.' });
            log.info(`Saved Equipment Type draft: ${type.id}`, { spaceId: type.spaceId });
            setEditingTypeId(undefined);
        } finally {
            setSaving(false);
        }
    }

    async function discardEditingType() {
        if (!editingTypeId) return;
        setSaving(true);
        try {
            if (await discardEquipmentType(editingTypeId)) setEditingTypeId(undefined);
        } finally {
            setSaving(false);
        }
    }

    // From every type, not the catalog's: a type retired from this form must keep its form until the retire returns.
    const editingType = allTypes.find((t) => t.id === editingTypeId) ?? null;
    const showForm = editingTypeId || editingTypeId === null;

    return (
        <PageShell>
            <PageHeader
                title="Equipment Types"
                description="The catalog the app builds guides and equipment from, and the custom types people asked for that it doesn't cover yet."
            >
                <Button type="button" onClick={() => setEditingTypeId(null)}>
                    Add New
                </Button>
            </PageHeader>

            <TabNav
                tabs={[
                    {
                        label: 'Catalog',
                        to: `/equipment/types/catalog${tabSearch}`,
                        count: typesLoaded ? types.length : undefined,
                    },
                    {
                        label: 'Candidates',
                        to: `/equipment/types/candidates${tabSearch}`,
                        count: candidatesLoadable.state === 'hasData' ? candidates.length : undefined,
                    },
                    {
                        label: 'Retired',
                        to: `/equipment/types/retired${tabSearch}`,
                        count: liveLoadable.state === 'hasData' ? retiredTypes.length : undefined,
                    },
                    {
                        label: 'Families',
                        to: `${FAMILIES_PATH}${tabSearch}`,
                        count: familiesLoadable.state === 'hasData' ? families.length : undefined,
                    },
                ]}
            />

            {tab === 'catalog' && <CatalogTab types={types} loaded={typesLoaded} onEdit={setEditingTypeId} />}
            {tab === 'candidates' && (
                <CandidatesTab candidates={candidates} loaded={candidatesLoadable.state === 'hasData'} />
            )}
            {tab === 'retired' && <RetiredTab types={retiredTypes} loaded={liveLoadable.state === 'hasData'} />}
            {tab === 'families' && <FamiliesTab />}

            {showForm && (
                <Dialog open onOpenChange={() => setEditingTypeId(undefined)}>
                    <DialogContent className="max-h-[80vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle>{editingType?.title || 'Create New Equipment Type'}</DialogTitle>
                            <DialogDescription className="m-2">
                                Enter or update the details for the Equipment Type.
                            </DialogDescription>
                        </DialogHeader>
                        <EquipmentTypeForm
                            spaceId={spaceFilter}
                            loading={saving}
                            type={editingType}
                            onSave={saveEquipmentType}
                            onDelete={discardEditingType}
                            onRetired={() => setEditingTypeId(undefined)}
                            onCancel={() => setEditingTypeId(undefined)}
                        />
                    </DialogContent>
                </Dialog>
            )}
        </PageShell>
    );
};
